import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import {
  deriveSpecies,
  matchesSpeciesFilter,
  type Camera,
  type Shot,
  type Species,
  type SpeciesFilterKey,
} from "../api/types";
import { wsBus } from "../api/wsBus";
import { AllChip, CameraChip } from "../components/Chips";
import { AnimateButton } from "../components/AnimationDialog";
import { ImageCard } from "../components/ImageCard";
import { Lightbox } from "../components/Lightbox";
import { SpeciesFilter } from "../components/SpeciesFilter";
import { fmtDateLong, fmtRelative, fmtTime, SPECIES_LABEL, SPECIES_ORDER } from "../components/format";

interface Props {
  newIds: Set<string>;
  onJumpToCalendar: (d: Date) => void;
  /** Unanswered Events. Zero below level 2 — they have no Schedule to be behind on. */
  pendingEvents: number;
  onOpenSchedule: () => void;
}

const TWENTY_FOUR_HOURS_MS = 24 * 3_600 * 1_000;
// Cameras with last_seen newer than this are reported as "online".
const ONLINE_WINDOW_MS = 60 * 60 * 1_000;

export function RecentScreen({ newIds, onJumpToCalendar, pendingEvents, onOpenSchedule }: Props) {
  const [shots, setShots] = useState<Shot[]>([]);
  // Dismissed for this visit only, deliberately not persisted: the events are
  // still unanswered tomorrow, and a strip that stays gone until localStorage
  // is cleared would quietly stop nagging the one person who needs it.
  const [stripDismissed, setStripDismissed] = useState(false);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [filter, setFilter] = useState<"all" | string>("all");
  const [speciesFilter, setSpeciesFilter] = useState<Set<SpeciesFilterKey>>(
    () => new Set(["moose"]),
  );
  const [loading, setLoading] = useState(true);
  // Screen-level lightbox so arrow-key navigation can hop between cards.
  const [openShotId, setOpenShotId] = useState<string | null>(null);

  const toggleSpecies = useCallback((k: SpeciesFilterKey) => {
    setSpeciesFilter((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    const from = new Date(Date.now() - TWENTY_FOUR_HOURS_MS).toISOString();
    const to = new Date().toISOString();
    const [page, cams] = await Promise.all([
      api.listImages({ from, to, limit: 200 }),
      api.listCameras(),
    ]);
    setShots(page.items);
    setCameras(cams);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refresh on every WS event — cheap, and ensures both new uploads and
  // analysis-completion changes flow through without a custom merge path.
  useEffect(() => {
    const unsub = wsBus.subscribe(() => {
      void refresh();
    });
    return unsub;
  }, [refresh]);

  // Camera filter applies first; species filter then narrows further.
  const cameraScoped = useMemo(
    () => (filter === "all" ? shots : shots.filter((s) => s.cameraId === filter)),
    [shots, filter],
  );

  const visible = useMemo(
    () => cameraScoped.filter((s) => matchesSpeciesFilter(s.analysis, speciesFilter)),
    [cameraScoped, speciesFilter],
  );

  const counts = useMemo(() => {
    const m: Record<string, number> = { all: shots.length };
    for (const c of cameras) m[c.cameraId] = shots.filter((s) => s.cameraId === c.cameraId).length;
    return m;
  }, [shots, cameras]);

  // Counts under the chips reflect the camera scope but ignore the species
  // filter — otherwise the count next to an unchecked chip would always read
  // zero, making it hard to know what's available. Bucketed by the chip the
  // card would display so totals tally exactly with what the user sees.
  const speciesFilterCounts = useMemo(() => {
    const m: Record<SpeciesFilterKey, number> = {
      "moose": 0, "roe-deer": 0, "wild-boar": 0, "other": 0,
    };
    for (const s of cameraScoped) {
      const sp = deriveSpecies(s.analysis);
      if (sp === "moose") m["moose"]++;
      else if (sp === "roe-deer") m["roe-deer"]++;
      else if (sp === "wild-boar") m["wild-boar"]++;
      else if (sp === "other") m["other"]++;
      else if (sp === null && s.analysis.state === "done") m["other"]++;
    }
    return m;
  }, [cameraScoped]);

  const speciesCounts = useMemo(() => {
    const m: Record<Species | "untagged", number> = {
      "moose": 0,
      "roe-deer": 0,
      "wild-boar": 0,
      "other": 0,
      "untagged": 0,
    };
    for (const s of shots) {
      const sp = deriveSpecies(s.analysis);
      m[sp ?? "untagged"]++;
    }
    return m;
  }, [shots]);

  const onlineCount = useMemo(() => {
    const cutoff = Date.now() - ONLINE_WINDOW_MS;
    return cameras.filter((c) => new Date(c.lastSeen).getTime() >= cutoff).length;
  }, [cameras]);

  const onShotChanged = useCallback((updated: Shot) => {
    setShots((prev) => prev.map((s) => (s.imageId === updated.imageId ? updated : s)));
  }, []);

  const onShotHidden = useCallback((imageId: string) => {
    setShots((prev) => prev.filter((s) => s.imageId !== imageId));
  }, []);

  const featured = shots[0];
  const rest = visible.slice(1, 25);
  const rows: Shot[][] = [];
  for (let i = 0; i < rest.length; i += 3) rows.push(rest.slice(i, i + 3));

  // Display order matches what's rendered: hero first, then the grid rows.
  const displayedShots = featured ? [featured, ...rest] : rest;
  const openIndex = openShotId
    ? displayedShots.findIndex((s) => s.imageId === openShotId)
    : -1;
  const openShot = openIndex >= 0 ? displayedShots[openIndex] : null;

  // Rendered above every branch below, including the empty and loading states:
  // "you owe three people an answer" is not less true on a day the cameras saw
  // nothing.
  const strip =
    pendingEvents > 0 && !stripDismissed ? (
      <div className="pending-strip" role="status">
        <span className="pending-strip-text">
          {pendingEvents === 1
            ? "1 event needs your answer"
            : `${pendingEvents} events need your answer`}
        </span>
        <button type="button" className="chip" onClick={onOpenSchedule}>
          Open the Schedule
        </button>
        <button
          type="button"
          className="pending-strip-close"
          onClick={() => setStripDismissed(true)}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    ) : null;

  if (loading) {
    return (
      <>
        {strip}
        <div className="empty-day" style={{ marginTop: 80 }}>
          <div className="glyph">Loading…</div>
        </div>
      </>
    );
  }

  if (!featured) {
    return (
      <>
        {strip}
        <div className="empty-day" style={{ marginTop: 80 }}>
          <div className="glyph">A quiet stretch.</div>
          <div>No images in the last 24 hours.</div>
        </div>
      </>
    );
  }

  const featuredCaptured = new Date(featured.capturedAt);
  const featuredSpecies = deriveSpecies(featured.analysis);

  return (
    <>
      {strip}
      <section className="hero">
        <div className="hero-feature">
          <ImageCard shot={featured} onChanged={onShotChanged} onHidden={onShotHidden} onOpen={(s) => setOpenShotId(s.imageId)} showNewBadge isNew={newIds.has(featured.imageId)} />
        </div>
        <div className="hero-side">
          <div className="eyebrow">Latest sighting · {fmtRelative(featuredCaptured)}</div>
          <h1 className="serif">
            {featuredSpecies ? SPECIES_LABEL[featuredSpecies] : "Untagged frame"}
            <span style={{ color: "var(--accent)" }}>.</span>
          </h1>
          <p className="lede">
            Captured at <em>{featured.cameraId}</em> on {fmtDateLong(featuredCaptured)},{" "}
            {fmtTime(featuredCaptured)}.
          </p>

          <div className="hero-stats">
            <div className="stat">
              <div className="v">
                {shots.length}
                <sup>↑</sup>
              </div>
              <div className="k">Frames · last 24h</div>
            </div>
            <div className="stat">
              <div className="v">{speciesCounts.moose}</div>
              <div className="k">Moose sightings</div>
            </div>
            <div className="stat">
              <div className="v">
                {onlineCount}
                <span className="mute" style={{ fontSize: "0.45em", marginLeft: "4px" }}>
                  / {cameras.length}
                </span>
              </div>
              <div className="k">Cameras online</div>
            </div>
            <div className="stat">
              <div className="v">
                {shots.filter((s) => {
                  const d = new Date(s.capturedAt);
                  const now = new Date();
                  return (
                    d.getFullYear() === now.getFullYear() &&
                    d.getMonth() === now.getMonth() &&
                    d.getDate() === now.getDate()
                  );
                }).length}
              </div>
              <div className="k">So far today</div>
            </div>
          </div>
        </div>
      </section>

      <div className="tag-row">
        {SPECIES_ORDER.map(
          (k) =>
            speciesCounts[k] > 0 && (
              <span className="t" key={k}>
                <span
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background:
                      k === "moose"
                        ? "var(--accent)"
                        : k === "roe-deer"
                          ? "var(--birch)"
                          : k === "wild-boar"
                            ? "#b97a4a"
                            : "var(--moss)",
                  }}
                />
                {SPECIES_LABEL[k]} <span className="ct">{speciesCounts[k]}</span>
              </span>
            ),
        )}
        {speciesCounts.untagged > 0 && (
          <span className="t">
            Untagged <span className="ct">{speciesCounts.untagged}</span>
          </span>
        )}
      </div>

      <div className="sec-hd" style={{ marginTop: "44px" }}>
        <div className="titles">
          <div className="eyebrow">Recent frames</div>
          <h2 className="serif">In the woods, lately.</h2>
        </div>
        <div className="meta" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span>
            Showing {visible.length} of {shots.length}
          </span>
          <AnimateButton shots={visible} sourceLabel="recent" />
        </div>
      </div>

      <div className="cam-chips" style={{ marginBottom: "10px" }}>
        <AllChip active={filter === "all"} count={counts.all} onClick={() => setFilter("all")} />
        {cameras.map((c) => (
          <CameraChip
            key={c.cameraId}
            camera={c}
            count={counts[c.cameraId] ?? 0}
            active={filter === c.cameraId}
            onClick={() => setFilter(c.cameraId)}
          />
        ))}
      </div>
      <div style={{ marginBottom: "24px" }}>
        <SpeciesFilter
          selected={speciesFilter}
          counts={speciesFilterCounts}
          onToggle={toggleSpecies}
        />
      </div>

      {rows.map((row, i) => (
        <div className="recent-row" key={i}>
          {row.map((shot) => (
            <ImageCard
              key={shot.imageId}
              shot={shot}
              onChanged={onShotChanged}
              onHidden={onShotHidden}
              onOpen={(s) => setOpenShotId(s.imageId)}
              showNewBadge
              isNew={newIds.has(shot.imageId)}
            />
          ))}
        </div>
      ))}

      {rest.length === 0 && (
        <div className="empty-day">
          <div className="glyph">Nothing else today.</div>
          <div>Try a different camera filter, or jump to the calendar.</div>
        </div>
      )}

      <div style={{ marginTop: "60px", textAlign: "center" }}>
        <button
          className="chip"
          onClick={() => onJumpToCalendar(new Date())}
          style={{ padding: "14px 22px" }}
        >
          Open today in calendar →
        </button>
      </div>

      {openShot && (
        <Lightbox
          shot={openShot}
          onClose={() => setOpenShotId(null)}
          onChanged={onShotChanged}
          onHidden={(id) => {
            // Advance past the hidden frame so the lightbox doesn't snap shut
            // mid-browse. If it was the last visible frame, just close.
            const i = displayedShots.findIndex((s) => s.imageId === id);
            const next =
              i >= 0 && i + 1 < displayedShots.length
                ? displayedShots[i + 1]
                : i > 0
                  ? displayedShots[i - 1]
                  : null;
            onShotHidden(id);
            setOpenShotId(next?.imageId ?? null);
          }}
          onPrev={openIndex > 0 ? () => setOpenShotId(displayedShots[openIndex - 1].imageId) : undefined}
          onNext={
            openIndex >= 0 && openIndex + 1 < displayedShots.length
              ? () => setOpenShotId(displayedShots[openIndex + 1].imageId)
              : undefined
          }
        />
      )}
    </>
  );
}
